import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, PaperPlaneTilt, ArrowsClockwise, Sparkle, ChatCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ActionButton({ action, onNavigate }) {
  if (!action || !action.url) return null;
  return (
    <button
      onClick={() => onNavigate(action.url)}
      data-testid="help-action-btn"
      className="mt-2 inline-flex items-center gap-1.5 text-[12px] bg-slate-900 text-white px-3 py-1.5 rounded-md hover:bg-slate-700 transition-colors"
    >
      <Sparkle size={11} weight="regular" /> {action.label || "Ir"}
    </button>
  );
}

function Bubble({ msg, onNavigate }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        data-testid={`help-msg-${msg.role}`}
        className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-slate-900 text-white"
            : "bg-slate-100 text-slate-800"
        }`}
      >
        {msg.text}
        {!isUser && <ActionButton action={msg.action} onNavigate={onNavigate} />}
      </div>
    </div>
  );
}

export default function HelpAssistant() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => uuid());
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [welcomed, setWelcomed] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // ── Draggable floating launcher ──────────────────────────────────────
  // Persist position across sessions in localStorage so the operator never
  // has to re-park the button. Position is stored as the bottom/right
  // offsets (in px) so we don't have to fight viewport resizes — the
  // launcher always stays anchored relative to the bottom-right corner.
  const STORAGE_KEY = "agentos_help_fab_pos";
  const [fabPos, setFabPos] = useState(() => {
    if (typeof window === "undefined") return { right: 24, bottom: 24 };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { right: 24, bottom: 24 };
  });
  const dragState = useRef({ active: false, startX: 0, startY: 0, startRight: 0, startBottom: 0, moved: false });

  const onPointerDown = (e) => {
    // Always start tracking so we can detect taps even when the panel
    // is open (operator clicks the X-shaped FAB to close). Drag is
    // intentionally suppressed while open — otherwise the panel would
    // detach from the launcher.
    dragState.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startRight: fabPos.right,
      startBottom: fabPos.bottom,
      moved: false,
      panelOpen: open,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragState.current.active) return;
    if (dragState.current.panelOpen) return; // freeze drag while panel is open
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragState.current.moved = true;
    if (!dragState.current.moved) return;
    const newRight = Math.max(8, Math.min(window.innerWidth - 60, dragState.current.startRight - dx));
    const newBottom = Math.max(8, Math.min(window.innerHeight - 60, dragState.current.startBottom - dy));
    setFabPos({ right: newRight, bottom: newBottom });
  };

  const onPointerUp = (e) => {
    if (!dragState.current.active) return;
    const wasDrag = dragState.current.moved;
    dragState.current.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (wasDrag) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fabPos)); } catch { /* ignore */ }
    } else {
      setOpen((v) => !v);
    }
  };

  useEffect(() => {
    if (!open || welcomed) return;
    api.get("/help/welcome").then(({ data }) => {
      setMessages([{ role: "assistant", text: data.reply, action: data.action }]);
      setSuggestions(data.suggestions || []);
      setWelcomed(true);
    }).catch(() => {
      setMessages([{ role: "assistant", text: "Olá! Como posso te ajudar?" }]);
      setWelcomed(true);
    });
  }, [open, welcomed]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message }]);
    setSuggestions([]);
    setSending(true);
    try {
      const { data } = await api.post("/help/chat", { session_id: sessionId, message });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, action: data.action }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "Tive um problema. Pode tentar de novo?",
      }]);
    } finally {
      setSending(false);
    }
  };

  const onNavigate = (url) => {
    // Whitelist: only navigate to internal /app/* routes returned by the LLM.
    if (typeof url !== "string" || !url.startsWith("/app/")) return;
    navigate(url);
    setOpen(false);
  };

  const reset = async () => {
    try { await api.post("/help/reset", { session_id: sessionId }); } catch {}
    setMessages([]);
    setSuggestions([]);
    setWelcomed(false);
  };

  return (
    <>
      {/* Floating support button — DRAGGABLE.
         Press and hold to drag; release on the same spot to open/close the
         panel; release after dragging to park it elsewhere. Position is
         persisted in localStorage so the operator's preference sticks. */}
      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        data-testid="help-fab"
        aria-label="Suporte AgentUS — arraste pra mover, clique pra abrir"
        style={{ right: `${fabPos.right}px`, bottom: `${fabPos.bottom}px`, touchAction: "none" }}
        className={`fixed z-50 group transition-transform duration-200 ease-out select-none ${
          open
            ? "scale-95"
            : "hover:-translate-y-0.5 active:scale-95 cursor-grab active:cursor-grabbing"
        }`}
      >
        {open ? (
          <span className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-lg">
            <X size={18} weight="regular" />
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-2 pl-3.5 pr-4 py-2.5 rounded-full bg-slate-900 text-white shadow-[0_8px_24px_-6px_rgba(15,23,42,0.45)] hover:shadow-[0_12px_32px_-6px_rgba(15,23,42,0.55)] hover:bg-slate-800 transition-all duration-300 ease-out relative"
          >
            {/* Subtle pulse ring */}
            <span aria-hidden className="absolute inset-0 rounded-full bg-slate-900 opacity-20 animate-ping" />
            <span className="relative flex items-center gap-2">
              <ChatCircle size={18} weight="fill" className="opacity-90" />
              <span className="text-[13px] font-medium tracking-tight">Suporte</span>
            </span>
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="help-panel"
          className="fixed bottom-24 right-6 w-[380px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-8rem)] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
        >
          <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-900 text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center">
                <Sparkle size={14} weight="regular" />
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold tracking-tight leading-none">Suporte AgentUS</h4>
                <p className="text-[10px] text-slate-400 mt-1">Dúvidas sobre o sistema</p>
              </div>
            </div>
            {/* Action cluster: "Limpar conversa" (↻) + "Fechar" (X).
              The close button was missing — operators were forced to hunt
              for the launcher FAB outside the panel just to dismiss the
              chat. Now there's an obvious X that mirrors every modern
              chat widget (Intercom, Crisp, etc.). */}
            <div className="flex items-center gap-1">
              <button
                onClick={reset}
                data-testid="help-reset"
                className="rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors p-1.5"
                title="Limpar conversa"
                aria-label="Limpar conversa"
              >
                <ArrowsClockwise size={14} />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="help-close"
                className="rounded-md text-slate-300 hover:text-white hover:bg-white/10 transition-colors p-1.5"
                title="Fechar"
                aria-label="Fechar painel de suporte"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
            {messages.map((m, i) => <Bubble key={i} msg={m} onNavigate={onNavigate} />)}
            {sending && (
              <div className="flex justify-start mb-3">
                <div className="bg-slate-100 rounded-lg px-3.5 py-2.5 text-sm text-slate-500">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}

            {suggestions.length > 0 && messages.length <= 1 && (
              <div className="mt-3 space-y-2" data-testid="help-suggestions">
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-medium">Perguntas comuns</p>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s.prompt)}
                    data-testid={`help-suggestion-${i}`}
                    className="w-full text-left text-[13px] text-slate-700 px-3 py-2 rounded-md border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-slate-100 p-3 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pergunte sobre o AgentUS…"
              data-testid="help-input"
              disabled={sending}
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="help-send"
              className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <PaperPlaneTilt size={14} weight="regular" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
